import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { fracao, parseFracao } from '../lib/format';
import { navigate } from '../router';
import styles from './screens.module.css';

interface Unidade {
  id: number;
  numero: string;
  fracaoIdeal: number;
  ativo: boolean;
}

interface ListaUnidades {
  unidades: Unidade[];
  /** Soma das frações ativas; `ok=false` quando diverge do total além da tolerância. */
  fracao: { soma: number; total: number; ok: boolean };
}

interface RespostaUnidade {
  unidade?: Unidade;
  error?: string;
}

type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; dados: ListaUnidades };

type Campo = 'numero' | 'fracao';

/** Erros de validação da API que pertencem a um campo do formulário. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  numero_duplicado: { campo: 'numero', mensagem: 'Já existe uma unidade com esse número.' },
  invalid_numero: {
    campo: 'numero',
    mensagem: 'Informe o número da unidade (até 20 caracteres).',
  },
  invalid_fracao: {
    campo: 'fracao',
    mensagem: 'A fração deve ser maior que 0 e no máximo 1,000000.',
  },
};

/** Demais erros da API (código → mensagem), mostrados acima da lista. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  not_found: 'Unidade não encontrada. A lista foi atualizada.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível salvar. Tente novamente.';

/**
 * Unidades do condomínio e fração ideal (RF-A02). `editavel` só para o síndico de
 * condomínio ativo: POST/PATCH são `requireRole('sindico')`, o contábil é
 * somente leitura e o tenant suspenso também (#127).
 */
export function Unidades({ editavel }: { editavel: boolean }) {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  // Só o id: a unidade em edição vem sempre da lista atual, então ativar/desativar
  // com o formulário aberto não é desfeito pelo Salvar.
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [aviso, setAviso] = useState<{ kind: 'success' | 'danger'; texto: string } | null>(null);

  const carregar = useCallback(async () => {
    const dados = await getJson<ListaUnidades>('/api/unidades');
    setCarga(dados ? { estado: 'ok', dados } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Erro da API fora dos campos; unidade inexistente fecha a edição e recarrega. */
  async function tratarFalha(codigo?: string) {
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
    if (codigo !== 'not_found') return;
    setEditandoId(null);
    await carregar();
  }

  async function alternarAtivo(u: Unidade) {
    setAviso(null);
    // O PATCH exige o registro completo: sem `ativo`, a API reativa a unidade.
    const res = await mutateJson<RespostaUnidade>(
      `/api/unidades/${encodeURIComponent(u.id)}`,
      { numero: u.numero, fracaoIdeal: u.fracaoIdeal, ativo: !u.ativo },
      'PATCH',
    );
    if (res.ok) await carregar();
    else await tratarFalha(res.data?.error);
  }

  async function aoSalvar(numero: string) {
    setAviso({ kind: 'success', texto: `Unidade ${numero} salva.` });
    setEditandoId(null);
    await carregar();
  }

  if (carga.estado === 'carregando') return <Spinner />;
  if (carga.estado === 'erro') {
    return <Alert kind="danger">Não foi possível carregar as unidades.</Alert>;
  }

  const { unidades, fracao: soma } = carga.dados;
  const emEdicao = unidades.find((u) => u.id === editandoId) ?? null;
  return (
    <div className={styles.stack}>
      <p>
        Soma das frações ativas: {fracao(soma.soma)} de {fracao(soma.total)}
      </p>
      {soma.ok ? null : (
        <Alert kind="warning">
          A soma das frações ativas ({fracao(soma.soma)}) difere de {fracao(soma.total)}. O rateio
          usa as frações cadastradas; revise as unidades.
        </Alert>
      )}
      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      {editavel ? (
        <UnidadeForm
          key={emEdicao?.id ?? 'nova'}
          unidade={emEdicao}
          onSalva={aoSalvar}
          onCancelar={() => setEditandoId(null)}
          onErroGeral={tratarFalha}
        />
      ) : null}

      {unidades.length === 0 ? (
        <EmptyState>Nenhuma unidade cadastrada.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Número</th>
                <th className={styles.num}>Fração ideal</th>
                <th>Situação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {unidades.map((u) => (
                <tr key={u.id}>
                  <td>{u.numero}</td>
                  <td className={styles.num}>{fracao(u.fracaoIdeal)}</td>
                  <td>
                    <Badge tone={u.ativo ? 'success' : 'warning'}>
                      {u.ativo ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td>
                    <div className={styles.row}>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Vínculos da unidade ${u.numero}`}
                        onClick={() => navigate(`/vinculos?unidade=${u.id}`)}
                      >
                        Vínculos
                      </Button>
                      {editavel ? (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label={`Editar unidade ${u.numero}`}
                            onClick={() => {
                              setAviso(null);
                              setEditandoId(u.id);
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`${u.ativo ? 'Desativar' : 'Ativar'} unidade ${u.numero}`}
                            onClick={() => void alternarAtivo(u)}
                          >
                            {u.ativo ? 'Desativar' : 'Ativar'}
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Formulário de criação (`unidade` nulo) ou edição; erros de validação vão para o campo. */
function UnidadeForm({
  unidade,
  onSalva,
  onCancelar,
  onErroGeral,
}: {
  unidade: Unidade | null;
  onSalva: (numero: string) => Promise<void>;
  onCancelar: () => void;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [numero, setNumero] = useState(unidade?.numero ?? '');
  const [fracaoTexto, setFracaoTexto] = useState(unidade ? fracao(unidade.fracaoIdeal) : '');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const fracaoIdeal = parseFracao(fracaoTexto);
    if (fracaoIdeal === null) {
      setErros({ fracao: 'Use o formato 0,131968 (até 6 casas).' });
      return;
    }
    setErros({});
    setEnviando(true);
    const res = unidade
      ? await mutateJson<RespostaUnidade>(
          `/api/unidades/${encodeURIComponent(unidade.id)}`,
          { numero, fracaoIdeal, ativo: unidade.ativo },
          'PATCH',
        )
      : await mutateJson<RespostaUnidade>('/api/unidades', { numero, fracaoIdeal });
    setEnviando(false);

    if (res.ok) {
      if (!unidade) {
        setNumero('');
        setFracaoTexto('');
      }
      await onSalva(res.data?.unidade?.numero ?? numero.trim());
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title={unidade ? `Editar unidade ${unidade.numero}` : 'Nova unidade'}>
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Número" error={erros.numero}>
            {(id) => (
              <Input
                id={id}
                value={numero}
                maxLength={20}
                onChange={(e) => setNumero(e.target.value)}
              />
            )}
          </Field>
          <Field label="Fração ideal" error={erros.fracao}>
            {(id) => (
              <Input
                id={id}
                value={fracaoTexto}
                inputMode="decimal"
                placeholder="0,131968"
                onChange={(e) => setFracaoTexto(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Button type="submit" disabled={enviando}>
            {unidade ? 'Salvar' : 'Adicionar'}
          </Button>
          {unidade ? (
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
